import { commonMessages } from '@common/intl'
import NotFound from '@common/NotFound'
import AppLayout from '@components/AppLayout'
import { Permission, UserRole } from '@gql'
import useAppState from '@hooks/useAppState'
import { appSection } from '@sections/apps/urls'
import { useAuth } from '@sections/auth/AuthProvider'
import SectionRoute from '@sections/auth/components/SectionRoute'
import { businessEntitySection } from '@sections/businessEntities/urls'
import { caseSection } from '@sections/cases/urls'
import { channelSection } from '@sections/channels/urls'
import ConfigurationSection, { configurationMenuUrl } from '@sections/configuration'
import { getConfigMenuItemsPermissions } from '@sections/configuration/utils'
import { customerSection } from '@sections/customers/urls'
import { financialProviderSection } from '@sections/financialProviders/urls'
import { leadQueueSection } from '@sections/leadQueues/urls'
import { messagingSection } from '@sections/messaging/urls'
import { oversightScenarioSection } from '@sections/oversightScenarios/urls'
import { playbookSection } from '@sections/playbooks/urls'
import { reportSection } from '@sections/reports/urls'
import { safetyTermSection } from '@sections/safetyTerms/urls'
import { settingsSection } from '@sections/settings/urls'
import { ticketSection } from '@sections/tickets/urls'
import { userSection } from '@sections/users/urls'
import { userTeamSection } from '@sections/userTeams/urls'
import AppLoader from '@ui/AppLoader'
import ErrorPage from '@ui/ErrorPage'
import FullscreenLoader from '@ui/FullscreenLoader'
import { WindowTitle } from '@ui/WindowTitle'
import { matchAnyPath } from '@utils/urls'
import React, { Suspense } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useIntl } from 'react-intl'
import { Route, Routes } from 'react-router'

const AuthSection = React.lazy(() => import('./auth'))
const AppSection = React.lazy(() => import('./apps'))
const BusinessEntitySection = React.lazy(() => import('./businessEntities'))
const CaseSection = React.lazy(() => import('./cases'))
const ChannelSection = React.lazy(() => import('./channels'))
const PlaybookSection = React.lazy(() => import('./playbooks'))
const OversightScenarioSection = React.lazy(() => import('./oversightScenarios'))
const SettingsSection = React.lazy(() => import('./settings'))
const CustomerSection = React.lazy(() => import('./customers'))
const HomeSection = React.lazy(() => import('./home'))
const FinancialProviderSection = React.lazy(() => import('./financialProviders'))
const TicketSection = React.lazy(() => import('./tickets'))
const MessagingSection = React.lazy(() => import('./messaging'))
const UserSection = React.lazy(() => import('./users'))
const UserTeamSection = React.lazy(() => import('./userTeams'))
const ReportsSection = React.lazy(() => import('./reports'))
const LeadQueueSection = React.lazy(() => import('./leadQueues'))
const SafetyTermSection = React.lazy(() => import('./safetyTerms'))

const AppRoutes: React.FC = () => {
  const intl = useIntl()
  const [appState, dispatchAppState] = useAppState()
  const { isAuthenticated, isTokenAuthLoading, isTokenVerifyLoading } = useAuth()

  const isAuthenticating = isTokenAuthLoading || isTokenVerifyLoading
  return (
    <>
      <WindowTitle title={intl.formatMessage(commonMessages.homeTitle)} />
      {isAuthenticating ? (
        <FullscreenLoader />
      ) : isAuthenticated ? (
        <AppLayout>
          <ErrorBoundary
            onError={() => {
              dispatchAppState({
                payload: {
                  error: 'unhandled',
                },
                type: 'displayError',
              })
            }}
            fallback={<AppLoader />}
          >
            <Suspense fallback={<AppLoader />}>
              <Routes>
                <Route
                  index
                  element={
                    <SectionRoute>
                      <HomeSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(businessEntitySection)}
                  element={<BusinessEntitySection />}
                />
                <Route
                  path={matchAnyPath(caseSection)}
                  element={
                    <SectionRoute>
                      <CaseSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(customerSection)}
                  element={
                    <SectionRoute>
                      <CustomerSection />
                    </SectionRoute>
                  }
                />
                <Route path={matchAnyPath(userSection)} element={<UserSection />} />
                <Route path={matchAnyPath(userTeamSection)} element={<UserTeamSection />} />
                <Route
                  path={matchAnyPath(appSection)}
                  element={
                    <SectionRoute permissions={[Permission.MANAGE_APPS]}>
                      <AppSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(settingsSection)}
                  element={
                    <SectionRoute permissions={[Permission.MANAGE_SETTINGS]}>
                      <SettingsSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(channelSection)}
                  element={
                    <SectionRoute permissions={[Permission.MANAGE_CHANNELS]}>
                      <ChannelSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(playbookSection)}
                  element={
                    <SectionRoute permissions={[Permission.MANAGE_PLAYBOOKS]}>
                      <PlaybookSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(oversightScenarioSection)}
                  element={
                    <SectionRoute permissions={[Permission.MANAGE_PLAYBOOKS]}>
                      <OversightScenarioSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(safetyTermSection)}
                  element={
                    // Its OWN top-level section, deliberately not a page under
                    // /settings: that whole subtree is gated on
                    // MANAGE_SETTINGS, so hosting it there would make
                    // MANAGE_SAFETY_POLICY necessary but not sufficient and a
                    // safety officer could not reach their own screen. The
                    // Configuration menu item (sections/configuration) is what
                    // links to it, and the menu's union gate opens the nav for
                    // whoever holds this permission.
                    <SectionRoute permissions={[Permission.MANAGE_SAFETY_POLICY]}>
                      <SafetyTermSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(financialProviderSection)}
                  element={
                    <SectionRoute permissions={[Permission.MANAGE_FINANCIAL_PROVIDERS]}>
                      <FinancialProviderSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={configurationMenuUrl}
                  element={
                    <SectionRoute
                      matchAccess="any"
                      permissions={getConfigMenuItemsPermissions(intl)}
                    >
                      <ConfigurationSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(reportSection)}
                  element={
                    <SectionRoute
                      permissions={[
                        Permission.MANAGE_CASES,
                        Permission.READ_ACQUISITION,
                        Permission.READ_FINANCE,
                      ]}
                      roles={[UserRole.TEAMLEADER]}
                      matchAccess="any"
                    >
                      <ReportsSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(leadQueueSection)}
                  element={
                    // MANAGE_CASES matches the quarantine, the one queue left;
                    // its reads and actions are gated again server-side.
                    <SectionRoute permissions={[Permission.MANAGE_CASES]}>
                      <LeadQueueSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(ticketSection)}
                  element={
                    <SectionRoute>
                      <TicketSection />
                    </SectionRoute>
                  }
                />
                <Route
                  path={matchAnyPath(messagingSection)}
                  element={
                    <SectionRoute permissions={[Permission.USE_MESSAGING]}>
                      <MessagingSection />
                    </SectionRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </AppLayout>
      ) : appState.error ? (
        appState.error === 'unhandled' && (
          <ErrorPage
            minHeight="100vh"
            onBack={() => window.location.assign('/')}
            onRefresh={() => window.location.reload()}
          />
        )
      ) : (
        <Suspense fallback={<FullscreenLoader />}>
          <AuthSection />
        </Suspense>
      )}
    </>
  )
}

AppRoutes.displayName = 'AppRoutes'
export default AppRoutes
